import React, { useEffect, useState, useCallback } from 'react';
import { 
  Container, Card, CardContent, 
  Typography, CardActionArea, Box, Chip, CircularProgress,
  Divider, TextField, InputAdornment, IconButton, Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

const CategorySelect = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); // 검색어 입력 상태
  const navigate = useNavigate();
  
  const THEME_NAVY = '#1a233a';
  const SUB_NAVY = '#2c3e50';

  /**
   * 카테고리 데이터 불러오기
   * 수정 포인트: search 파라미터를 명시적으로 받아 API에 전달합니다.
   */
  const fetchCategories = useCallback(async (searchQuery = searchTerm) => {
    const token = localStorage.getItem('token'); 
    if (!token) {
      console.error("인증 토큰이 없습니다. 로그인 페이지로 이동합니다.");
      navigate('/login'); 
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get('/api/category/active', {
        params: { search: searchQuery } // 백엔드의 @RequestParam과 매핑
      });
      setCategories(res.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        alert("세션이 만료되었거나 권한이 없습니다. 다시 로그인해주세요.");
        navigate('/login');
      }
      console.error("카테고리 로딩 실패", err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, navigate]);

  // [수정] 처음 진입 시에만 호출하도록 변경 (검색은 handleSearch에서 처리)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
        alert("로그인이 필요합니다.");
        navigate('/login');
        return;
    }
    fetchCategories('');
  }, [fetchCategories, navigate]); 

  // 검색 실행
  const handleSearch = () => {
    fetchCategories(searchTerm); 
  };

  // 검색 초기화
  const handleReset = () => {
    setSearchTerm('');
    fetchCategories(''); // 빈 문자열로 전체 조회(그룹 내 전체)
  };

  // 엔터키 검색 지원
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 로딩 중이면서 데이터가 아예 없는 초기 상태일 때만 풀스크린 로더 표시
  if (loading && categories.length === 0 && !searchTerm) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
      <CircularProgress sx={{ color: THEME_NAVY }} />
      <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>코스를 구성하는 중입니다...</Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 8 }}>
      <Container 
        maxWidth="lg" 
        disableGutters 
        sx={{ 
          pt: { xs: 4, sm: 6, md: 8 }, 
          px: { xs: 0, sm: 3 } 
        }}
      >
        {/* 헤더 섹션 */}
        <Box sx={{ mb: { xs: 4, md: 5 }, textAlign: 'center', px: 2 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 900, 
              color: THEME_NAVY, 
              mb: 2, 
              fontSize: { xs: '1.8rem', md: '3.2rem' },
              letterSpacing: '-1.5px'
            }}
          >
            수어 학습 코스
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', opacity: 0.8, fontSize: { xs: '0.95rem', md: '1.1rem' }, fontWeight: 400 }}>
            내 그룹에 할당된 맞춤형 카테고리를 학습하세요.
          </Typography>
        </Box>

        {/* 검색바 섹션 */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row', 
          justifyContent: 'center', 
          alignItems: 'stretch', 
          mb: { xs: 5, md: 8 }, 
          px: 2,
          gap: 1,
          maxWidth: '600px', 
          mx: 'auto'
        }}>
          <TextField
            placeholder="학습 코스 검색"
            variant="outlined"
            size="large"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ 
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                height: '100%',
                borderRadius: '50px',
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: THEME_NAVY },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: THEME_NAVY, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleReset}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ 
              borderRadius: '50px', 
              minWidth: { xs: '64px', sm: '100px' },
              px: { xs: 2, sm: 4 }, 
              height: 'auto', 
              bgcolor: THEME_NAVY,
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: SUB_NAVY, boxShadow: 'none' }
            }}
          >
            조회
          </Button>
        </Box>

        {/* 결과 표시 영역 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={30} sx={{ color: THEME_NAVY }} />
          </Box>
        ) : categories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              {searchTerm ? `"${searchTerm}"에 대한 검색 결과가 없습니다.` : "할당된 학습 코스가 없습니다."}
            </Typography>
            <Button 
              startIcon={<RefreshIcon />} 
              onClick={handleReset}
              sx={{ color: THEME_NAVY, fontWeight: 700 }}
            >
              전체 목록 보기
            </Button>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gap: { xs: '16px', sm: 4 },
            gridTemplateColumns: {
              xs: '1fr',           
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)'  
            },
            width: '100%',
            px: { xs: 2, sm: 0 }
          }}>
            {categories.map((category) => (
              <Card key={category.categoryPk} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                borderRadius: { xs: 4, sm: 5 }, 
                bgcolor: 'white',
                border: '1px solid',
                borderColor: '#e2e8f0', 
                boxShadow: { 
                  xs: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
                  sm: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' 
                },
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: { md: 'translateY(-12px)' }, 
                  boxShadow: { md: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' },
                  borderColor: THEME_NAVY 
                }
              }}>
                <CardActionArea 
                  onClick={() => navigate(`/study/${category.categoryPk}`)}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Box sx={{ 
                    width: '100%',
                    height: { xs: 140, md: 190 }, 
                    background: '#f8fafc', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderBottom: '1px solid #f1f5f9'
                  }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    }}>
                      <SchoolIcon sx={{ fontSize: { xs: 35, md: 45 }, color: THEME_NAVY }} />
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ p: { xs: 2.5, md: 4 }, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 800, 
                          color: THEME_NAVY, 
                          fontSize: { xs: '1.2rem', md: '1.45rem' },
                          lineHeight: 1.2
                        }}
                      >
                        {category.name}
                      </Typography>
                      <Chip 
                        label="학습" 
                        size="small" 
                        sx={{ 
                          bgcolor: THEME_NAVY, 
                          color: 'white', 
                          fontWeight: 700, 
                          fontSize: '0.65rem',
                          height: 20
                        }} 
                      />
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        height: '3.2em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.6,
                        mb: 2,
                        fontSize: '0.9rem'
                      }}
                    >
                      {category.description || `${category.name}의 기초부터 실전까지 차근차근 학습합니다.`}
                    </Typography>

                    <Divider sx={{ mb: 2.5, borderColor: '#f1f5f9' }} />

                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      color: THEME_NAVY 
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PlayCircleOutlineIcon sx={{ mr: 1, fontSize: '1.3rem' }} />
                        <Typography variant="button" sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                          학습 시작하기
                        </Typography>
                      </Box>
                      <ArrowForwardIosIcon sx={{ fontSize: '0.8rem', opacity: 0.6 }} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CategorySelect;